/**
 * 글(블로그) 목 데이터 — `/blog/[slug]` 와 Post Spotlights 가 같은 출처를 쓴다.
 * @description 제목 있는 글과 없는 글(본문이 제목 자리에 서는 글), 두 줄 넘는 제목, 이모지가
 *   앞에 오는 제목을 섞는다 — 카드의 자리 잡기가 실제로 시험되도록. 스포트라이트가 두
 *   페이지가 되게 스물 넘게 둔다. 본문은 마크다운이고 제목·목록·인용·표·그림을 한 번씩
 *   밟는다 — 에디토리얼 조판이 실제로 시험되도록.
 */
import type { BlogDetail, PostDetail } from "@/domains/core/post";

const author = (
  userId: number,
  username: string,
  displayName: string,
  profileImageUrl?: string,
) => ({
  userId,
  username,
  displayName,
  profileImageUrl,
});

const HELLEN = author(
  1,
  "hellen.aoki",
  "Hellen Aoki",
  "/assets/mock/birthday-cafe.webp",
);
const MINJI = author(5, "minji", "Min Ji", "/assets/mock/wall-b.jpg");
const YUKI = author(7, "yuki.dance", "Yuki", "/assets/mock/wall-a.jpg");
const SORA = author(8, "sora", "Sora", "/assets/images/hanuru/hanuru-29.webp");

const topic = (topicId: number, name: string) => ({ topicId, name });
const GEAR = topic(1, "Gear & Setup");
const WORKFLOW = topic(2, "Workflow");
const EVENT = topic(3, "Fan Event");
const PRINT = topic(4, "Print");

/** 글마다 다른 본문을 다 쓰지는 않는다 — 조판을 시험할 한 벌을 제목만 갈아 끼운다 */
const body = (title: string, thumbnail: string) => `
${title.startsWith("#") ? "" : "Every collab starts the same way: someone posts a half-finished idea and asks who's in. This one started in a group chat at 2 a.m., and by the weekend six of us had a plan."}

## What we were trying to make

We wanted something a fan could hold — not a screenshot, not a story that disappears in a day. The brief was short:

- **One weekend**, start to finish
- Everyone contributes something they can actually do
- Nothing that needs a budget bigger than a coffee run

> "If it takes longer than a weekend, it's a project. If it takes a weekend, it's a party."
> — Min Ji, the person who said this and then stayed up until 4 a.m. anyway

![${title}](${thumbnail})

## How the work was split

| Role | Who | What it took |
| --- | --- | --- |
| Editing | Hellen | 6 hours, two coffees |
| Illustration | Yuki | most of Saturday |
| Printing & pickup | Sora | one bus ride |

The trick was **not** to plan too much. We wrote down what each person owed and let the rest sort itself out in the chat.

### Things that went wrong

1. The print shop closed an hour earlier than the website said.
2. Half the photos were the wrong ratio and had to be re-cropped.
3. Nobody remembered to bring tape.

None of it mattered. The collab shipped, the people who came got something they could keep, and the chat is already arguing about the next one.

## If you want to try this

Start smaller than feels reasonable. Post the idea before it's ready. Fanwork's collab page will do the roster and the deadline for you — the only thing it can't do is the part where you actually show up.
`;

const post = (
  postId: number,
  slug: string,
  title: string | undefined,
  content: string | undefined,
  thumbnailUrl: string,
  by: PostDetail["author"],
  topics: PostDetail["topics"],
  createdAt: string,
): PostDetail => ({
  postId,
  slug,
  title: title ?? "",
  content: content ?? body(title ?? "Untitled", thumbnailUrl),
  thumbnailUrl,
  imageRatio: "3:2",
  author: by,
  topics,
  createdAt,
});

export const MOCK_POSTS: readonly PostDetail[] = [
  post(
    101,
    "creator-desk-setup-for-cover-video-editing",
    "Creator desk setup for cover video editing",
    undefined,
    "/assets/mock/wall-c.jpg",
    HELLEN,
    [GEAR],
    "2026-09-04T09:00:00Z",
  ),
  post(
    102,
    "home-recording-and-vocal-mix",
    "Home recording and vocal mix: how I prepare a cover",
    undefined,
    "/assets/mock/wall-d.jpg",
    MINJI,
    [WORKFLOW],
    "2026-09-03T09:00:00Z",
  ),
  post(
    103,
    "apobangpo-fan-sign-diary",
    undefined,
    "Apobangpo\n\n오늘 다녀왔어요 — 팬사인회 후기 올립니다.\n\n## 줄 서기\n\n새벽 다섯 시에 도착했는데 이미 앞에 스물 몇 명이 있었어요. 담요 챙기길 잘했다.\n\n## 사인\n\n말하고 싶은 걸 미리 적어 갔는데, 막상 앞에 서니 절반도 못 했어요. 그래도 웃어 주셔서 그걸로 충분.\n\n> 다음엔 꼭 편지 들고 가기.\n\n![팬사인회](/assets/mock/wall-1.webp)",
    "/assets/mock/wall-1.webp",
    YUKI,
    [EVENT],
    "2026-09-02T09:00:00Z",
  ),
  post(
    104,
    "busking-gear-checklist-six-member-dance-cover",
    "Busking gear checklist for a six-member dance cover",
    undefined,
    "/assets/mock/wall-a.jpg",
    SORA,
    [GEAR],
    "2026-09-01T09:00:00Z",
  ),
  post(
    105,
    "cup-sleeve-design-walkthrough",
    "✨ Cup sleeve design walkthrough",
    undefined,
    "/assets/mock/wall-2.png",
    HELLEN,
    [PRINT],
    "2026-08-30T09:00:00Z",
  ),
  post(
    106,
    "zine-layout-in-a-weekend",
    "Zine layout in a weekend",
    undefined,
    "/assets/mock/wall-b.jpg",
    MINJI,
    [PRINT],
    "2026-08-29T09:00:00Z",
  ),
  post(
    108,
    "fan-sign-photo-tips",
    "Fan sign photo tips",
    undefined,
    "/assets/mock/wall-3.webp",
    YUKI,
    [EVENT],
    "2026-08-27T09:00:00Z",
  ),
  post(
    109,
    "first-fan-meeting-what-i-wish-i-knew",
    undefined,
    "First time at a fan meeting — what I wish I knew before going.\n\n## Arrive earlier than the time on the ticket\n\nThe line forms before the doors are even mentioned.\n\n## Bring less\n\nYou will be standing for a long time. A small bag, a bottle of water, a portable charger. That's it.",
    "/assets/mock/wall-c.jpg",
    SORA,
    [EVENT],
    "2026-08-26T09:00:00Z",
  ),
  post(
    110,
    "photocard-sleeves-compared",
    "Photocard sleeves compared",
    undefined,
    "/assets/mock/wall-a.jpg",
    HELLEN,
    [GEAR],
    "2026-08-25T09:00:00Z",
  ),
  post(
    111,
    "how-our-crew-split-the-lyric-video-work",
    "How our crew split the lyric video work",
    undefined,
    "/assets/mock/wall-d.jpg",
    MINJI,
    [WORKFLOW],
    "2026-08-24T09:00:00Z",
  ),
  post(
    112,
    "seoul-stop-banner-behind-the-scenes",
    "Seoul stop banner: behind the scenes",
    undefined,
    "/assets/mock/wall-1.webp",
    YUKI,
    [PRINT],
    "2026-08-23T09:00:00Z",
  ),
  post(
    113,
    "printing-fan-art-at-home",
    "Printing fan art at home without the colors going off",
    undefined,
    "/assets/mock/wall-2.png",
    SORA,
    [PRINT],
    "2026-08-22T09:00:00Z",
  ),
  post(
    114,
    "practice-room-rental-guide",
    "Practice room rental guide",
    undefined,
    "/assets/mock/wall-b.jpg",
    HELLEN,
    [WORKFLOW],
    "2026-08-21T09:00:00Z",
  ),
  post(
    115,
    "lightstick-mods-that-are-actually-safe",
    "Lightstick mods that are actually safe",
    undefined,
    "/assets/mock/wall-3.webp",
    MINJI,
    [GEAR],
    "2026-08-20T09:00:00Z",
  ),
  post(
    116,
    "concert-goods-line-tips",
    undefined,
    "콘서트 굿즈 줄 서기 팁 — 새벽 4시에 가면 생기는 일.\n\n## 준비물\n\n- 접이식 의자\n- 보조배터리 두 개\n- 같이 줄 설 친구\n\n## 결론\n\n네 시는 이르지 않다.",
    "/assets/mock/wall-c.jpg",
    YUKI,
    [EVENT],
    "2026-08-19T09:00:00Z",
  ),
  post(
    117,
    "recording-a-dance-cover-with-one-phone",
    "Recording a dance cover with one phone",
    undefined,
    "/assets/mock/wall-d.jpg",
    SORA,
    [WORKFLOW],
    "2026-08-18T09:00:00Z",
  ),
  post(
    118,
    "trading-etiquette-a-short-list",
    "Trading etiquette: a short list",
    undefined,
    "/assets/mock/wall-a.jpg",
    HELLEN,
    [EVENT],
    "2026-08-17T09:00:00Z",
  ),
  post(
    119,
    "our-zines-print-run-by-the-numbers",
    "Our zine's print run, by the numbers",
    undefined,
    "/assets/mock/wall-2.png",
    MINJI,
    [PRINT],
    "2026-08-16T09:00:00Z",
  ),
  post(
    120,
    "cafe-event-decoration-checklist",
    "✨ Cafe event decoration checklist",
    undefined,
    "/assets/mock/wall-1.webp",
    YUKI,
    [EVENT],
    "2026-08-15T09:00:00Z",
  ),
  post(
    121,
    "vocal-warm-ups-before-a-busking-set",
    "Vocal warm-ups before a busking set",
    undefined,
    "/assets/mock/wall-b.jpg",
    SORA,
    [WORKFLOW],
    "2026-08-14T09:00:00Z",
  ),
  post(
    122,
    "how-we-picked-the-banner-slogan",
    "How we picked the banner slogan",
    undefined,
    "/assets/mock/wall-3.webp",
    HELLEN,
    [PRINT],
    "2026-08-13T09:00:00Z",
  ),
  post(
    123,
    "fan-meeting-outfit-ideas",
    "Fan meeting outfit ideas that photograph well",
    undefined,
    "/assets/mock/wall-c.jpg",
    MINJI,
    [EVENT],
    "2026-08-12T09:00:00Z",
  ),
  post(
    124,
    "a-weekend-zine-sprint-hour-by-hour",
    "A weekend zine sprint, hour by hour",
    undefined,
    "/assets/mock/wall-d.jpg",
    YUKI,
    [PRINT],
    "2026-08-11T09:00:00Z",
  ),
  post(
    125,
    "cheap-lighting-for-cover-videos",
    "Cheap lighting for cover videos",
    undefined,
    "/assets/mock/wall-a.jpg",
    SORA,
    [GEAR],
    "2026-08-10T09:00:00Z",
  ),
  post(
    126,
    "sleeve-printing-paper-weights-compared",
    "Sleeve printing: paper weights compared",
    undefined,
    "/assets/mock/wall-2.png",
    HELLEN,
    [PRINT],
    "2026-08-09T09:00:00Z",
  ),
];

/**
 * 블로그 목 데이터 — 서버 계약(`BlogDetail`) 모양.
 * @description 위의 [MOCK_POSTS] 를 옮겨 만든다. 목을 두 벌 손으로 쓰면 한쪽만 고치는 일이
 *   생긴다. 아티클로 두므로 본문은 마크다운이고 첨부 그림은 없다 — 아티클은 그림을 본문 안에
 *   마크다운으로 넣는다.
 */
export const MOCK_BLOG_ITEMS: readonly BlogDetail[] = MOCK_POSTS.map((post) => ({
  itemType: "ARTICLE",
  slug: post.slug,
  title: post.title,
  excerpt: post.content.replace(/\s+/g, " ").trim().slice(0, 160),
  coverImageUrl: post.thumbnailUrl ?? null,
  content: post.content,
  contentFormat: "MARKDOWN",
  imageUrls: [],
  author: {
    name: post.author.displayName,
    profileImageUrl: post.author.profileImageUrl ?? null,
  },
  publishedAt: post.createdAt,
  updatedAt: post.updatedAt ?? post.createdAt,
  viewCount: 0,
  readingMinutes: null,
  language: "EN",
  availableLanguages: ["EN"],
  category: post.topics?.[0]
    ? {
        categoryId: post.topics[0].topicId,
        name: post.topics[0].name,
        slug: String(post.topics[0].topicId),
      }
    : null,
}));
